import { useState, useMemo, useEffect, useCallback } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { apiFetch } from "../../context/AppContext";
import { Modal, DashboardStatCard, Pagination, AttachmentPreviewRow } from "../../components/common/UI";
import { SECTIONS, R_CFG } from "../../components/common/gapAssessmentSections";
import {
  gapRecommendationLabel,
  gapRecommendationSummary,
  gapRecommendationToExportQa,
  isGapRecommendationObject,
  gapAttachmentPreviewPath,
} from "../../components/common/gapRecommendationUtils";
import { ArcGauge, ScoreRing } from "../../components/common/ArcGauge";
import {
  scoreColor,
  scoreLabel,
  scoreBg,
} from "../../components/common/gapAssessmentTheme";
import {
  Shield,
  CheckCircle2,
  AlertTriangle,
  ClipboardList,
  Check,
  X,
  Minus,
  HelpCircle,
  Building2,
  Calendar,
  ChevronUp,
  ChevronDown,
  Search,
  FileText,
  Eye,
  ArrowLeft,
  X as XIcon,
  BarChart3,
  Users,
  ShieldCheck,
  ShieldAlert,
  FileDown,
  FileSpreadsheet,
} from "lucide-react";
import {
  exportSubmissionPdf,
  exportSubmissionExcel,
  safeExportFilename,
} from "../../utils/exportSubmissionPdf";
import { exportGapEvidenceExcel, exportGapEvidencePdf, fetchGapSubmission } from "../../utils/gapEvidenceExport";
import { showError } from "../../utils/sweetAlert";
import { useDepartments } from "../../hooks/useDepartments";

function plainTextNote(note) {
  if (!note) return "";
  if (!note.includes("<")) return note;
  try {
    const doc = new DOMParser().parseFromString(note, "text/html");
    return doc.body.textContent || "";
  } catch {
    return note;
  }
}

const SECTION_MAP = Object.fromEntries(SECTIONS.map((s) => [s.id, s]));

function DeptFilterSelect({ departments, value, onChange }) {
  if (!departments || departments.length <= 1) return null;

  return (
    <select
      value={value}
      onChange={(e) => onChange(e.target.value)}
      className="text-xs border border-slate-300 rounded-lg px-3 py-2 bg-white text-slate-800 min-w-[180px]"
    >
      <option value="">All departments</option>
      {departments.map((d) => (
        <option key={d.id} value={d.id}>
          {d.name}
        </option>
      ))}
    </select>
  );
}

export default function GapAssessmentSubmissions() {
  return <SubmissionsList />;
}

export function GapAssessmentResponsesPanel() {
  return <SubmissionsList embedded />;
}

function SubmissionsList({ embedded = false }) {
  const navigate = useNavigate();
  const { departments } = useDepartments();
  const [searchParams, setSearchParams] = useSearchParams();
  const [submissions, setSubmissions] = useState([]);
  const [apiStats, setApiStats] = useState({ total: 0, avg_score: 0, compliant: 0, critical: 0 });
  const [page, setPage] = useState(1);
  const [pages, setPages] = useState(1);
  const [total, setTotal] = useState(0);
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const PAGE_SIZE = 20;
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [search, setSearch] = useState("");
  const [deptFilter, setDeptFilter] = useState(() => searchParams.get("department_id") || "");
  const [statusFilter, setStatusFilter] = useState(""); // "", "compliant", "critical"
  const [sortKey, setSortKey] = useState("created_at");
  const [sortDir, setSortDir] = useState("desc");
  const [selected, setSelected] = useState(null);

  const fetchSubmissions = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const params = new URLSearchParams({
        page: String(page),
        page_size: String(PAGE_SIZE),
        sort: sortKey,
        sort_dir: sortDir,
      });
      if (debouncedSearch.trim()) params.set("search", debouncedSearch.trim());
      if (deptFilter) params.set("department_id", deptFilter);
      if (statusFilter) params.set("status", statusFilter);
      const res = await apiFetch(`/gap-assessment/submissions?${params}`);
      if (!res.ok) throw new Error("Failed to load submissions");
      const data = await res.json();
      setSubmissions(data.submissions || []);
      setTotal(data.total || 0);
      setPages(data.pages || 1);
      setApiStats(data.stats || { total: data.total || 0, avg_score: 0, compliant: 0, critical: 0 });
    } catch (e) {
      setError(e.message || "Could not load submissions");
    } finally {
      setLoading(false);
    }
  }, [page, debouncedSearch, deptFilter, statusFilter, sortKey, sortDir]);

  useEffect(() => { fetchSubmissions(); }, [fetchSubmissions]);

  useEffect(() => {
    const openId = searchParams.get("id");
    if (!openId) return undefined;
    const found = submissions.find((row) => row.id === openId);
    if (found) {
      setSelected(found);
      return undefined;
    }
    let cancelled = false;
    (async () => {
      try {
        const row = await fetchGapSubmission(openId);
        if (!cancelled) setSelected(row);
      } catch {
        // Leave the list visible if the id is missing or unauthorized.
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [searchParams, submissions]);

  useEffect(() => {
    const t = setTimeout(() => { setDebouncedSearch(search); setPage(1); }, 350);
    return () => clearTimeout(t);
  }, [search]);

  useEffect(() => { setPage(1); }, [deptFilter, sortKey, sortDir, statusFilter]);

  useEffect(() => {
    if (!searchParams.has("org")) return;
    const next = new URLSearchParams(searchParams);
    next.delete("org");
    setSearchParams(next, { replace: true });
  }, [searchParams, setSearchParams]);

  const stats = useMemo(() => ({
    total: apiStats.total ?? total,
    avgScore: apiStats.avg_score ?? 0,
    compliant: apiStats.compliant ?? 0,
    critical: apiStats.critical ?? 0,
  }), [apiStats, total]);

  const sorted = submissions;

  const toggleSort = (key) => {
    if (sortKey === key) setSortDir((d) => (d === "asc" ? "desc" : "asc"));
    else {
      setSortKey(key);
      setSortDir("asc");
    }
  };

  const SortIcon = ({ k }) => {
    if (sortKey !== k)
      return <span className="text-slate-300 text-[10px]">↕</span>;
    return sortDir === "asc" ? (
      <ChevronUp size={12} className="text-indigo-500" />
    ) : (
      <ChevronDown size={12} className="text-indigo-500" />
    );
  };

  return (
    <div
      className={embedded ? "space-y-3" : "bg-slate-100 font-sans min-h-[calc(100vh-3rem)]"}
    >
      {!embedded && (
        <header className="bg-white border-b border-slate-300 px-4 sm:px-6 py-3 flex items-center gap-3">
          <div className="flex items-center gap-3 min-w-0 flex-1">
            <div className="w-9 h-9 rounded-xl flex items-center justify-center shadow-md shrink-0 bg-gradient-to-br from-blue-500 to-indigo-500">
              <FileText size={18} className="text-white" />
            </div>
            <div className="min-w-0">
              <h1 className="text-base sm:text-lg font-bold text-slate-900 leading-tight">
                Gap Assessment Submissions
              </h1>
              <p className="text-xs text-slate-700 hidden sm:block">
                Completed assessments by department
              </p>
            </div>
          </div>

          <div className="hidden md:flex flex-1 justify-center max-w-sm px-2">
            <div className="flex items-center gap-2 w-full px-3 py-1.5 bg-white border border-slate-300 rounded-lg">
              <Search size={14} className="text-slate-400 flex-shrink-0" />
              <input
                autoComplete="off"
                autoCorrect="off"
                spellCheck="false"
                name="gap-submission-search"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Search by submitted by…"
                className="w-full border-0 outline-none text-xs text-slate-900 bg-transparent"
              />
            </div>
          </div>

          <div className="flex items-center gap-2 sm:gap-3 flex-shrink-0">
            <button
              onClick={() => navigate("/user/gap-assessment")}
              className="flex items-center gap-1.5 px-3 py-1.5 text-white text-xs font-semibold rounded-lg transition-all whitespace-nowrap shadow-md shadow-blue-500/35 bg-gradient-to-br from-blue-500 via-indigo-500 to-indigo-600 hover:shadow-lg hover:brightness-105 active:scale-[0.98]"
            >
              <ArrowLeft size={12} />
              <span className="hidden sm:inline">Back to Assessment</span>
              <span className="sm:hidden">Back</span>
            </button>
          </div>
        </header>
      )}

      <div className={embedded ? "space-y-3" : "p-3 sm:p-4 space-y-3"}>
        {embedded && (
          <div className="flex flex-col sm:flex-row gap-2 sm:items-center">
            <div className="flex items-center gap-2 flex-1 px-3 py-2 bg-white border border-slate-200 rounded-lg">
              <Search size={14} className="text-slate-400 flex-shrink-0" />
              <input
                autoComplete="off"
                autoCorrect="off"
                spellCheck="false"
                name="gap-submission-search"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Search by submitted by…"
                className="w-full border-0 outline-none text-xs text-slate-900 bg-transparent"
              />
            </div>
            <DeptFilterSelect departments={departments} value={deptFilter} onChange={setDeptFilter} />
          </div>
        )}
        {!embedded && (
          <div className="flex flex-col sm:flex-row gap-2 sm:items-center sm:justify-between">
            <div className="md:hidden flex items-center gap-2 w-full px-3 py-2 bg-white border border-slate-300 rounded-lg">
              <Search size={14} className="text-slate-400 flex-shrink-0" />
              <input
                autoComplete="off"
                autoCorrect="off"
                spellCheck="false"
                name="gap-submission-search"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Search by submitted by…"
                className="w-full border-0 outline-none text-xs text-slate-900 bg-transparent"
              />
            </div>
            <DeptFilterSelect departments={departments} value={deptFilter} onChange={setDeptFilter} />
          </div>
        )}

        <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
          <DashboardStatCard
            title="Submissions"
            value={stats.total}
            subtitle="Completed assessments"
            accent="#7dd3fc"
            icon={Users}
            loading={loading}
            active={statusFilter === ""}
            onClick={() => setStatusFilter("")}
          />
          <DashboardStatCard
            title="Average Score"
            value={`${stats.avgScore}%`}
            subtitle="Across listed submissions"
            accent="#a5b4fc"
            icon={BarChart3}
            loading={loading}
            tooltip="The average of the assessments in this list. Each score is the percentage of points that submission earned."
          />
          <DashboardStatCard
            title="Compliant"
            value={stats.compliant}
            subtitle="Score ≥ 75%"
            accent="#6ee7b7"
            icon={ShieldCheck}
            loading={loading}
            active={statusFilter === "compliant"}
            onClick={() => setStatusFilter("compliant")}
          />
          <DashboardStatCard
            title="Critical"
            value={stats.critical}
            subtitle="Score below 50%"
            accent="#fca5a5"
            icon={ShieldAlert}
            loading={loading}
            active={statusFilter === "critical"}
            onClick={() => setStatusFilter("critical")}
          />
        </div>

        <div className="bg-white rounded-xl border border-slate-200 shadow-sm overflow-hidden">
          {loading ? (
            <div className="py-16 text-center">
              <div className="w-10 h-10 rounded-full mx-auto mb-4 border-[3px] border-indigo-500/15 border-t-indigo-500 animate-spin" />
              <p className="text-[13px] text-slate-600">Loading submissions…</p>
            </div>
          ) : error ? (
            <div className="py-12 text-center">
              <AlertTriangle size={32} className="text-red-500 mx-auto mb-3 block" />
              <p className="text-[13px] text-red-700 font-semibold">{error}</p>
            </div>
          ) : sorted.length === 0 ? (
            <div className="py-16 text-center">
              <ClipboardList size={40} className="text-slate-300 mx-auto mb-3.5 block" />
              <p className="text-sm font-semibold text-slate-600">
                {search ? "No matching submissions" : "No submissions yet"}
              </p>
              <p className="text-xs text-slate-400 mt-1.5">
                {search
                  ? "Try a different search term"
                  : "Completed assessments will appear here"}
              </p>
            </div>
          ) : (
            <>
            <div className="w-full max-w-full overflow-auto scrollbar-thin max-h-[calc(3.5rem+5*4rem)]">
              <table className="w-full border-collapse text-[13px]">
                <thead className="sticky top-0 z-10 bg-slate-50">
                  <tr className="bg-slate-50 border-b-2 border-indigo-500/10">
                    <th className="px-3 py-3.5 text-left font-bold text-[11px] text-slate-600 uppercase tracking-wider whitespace-nowrap w-12">
                      S.No.
                    </th>
                    {[
                      { k: "org_name", label: "Organisation" },
                      { k: "department_name", label: "Department" },
                      { k: "submitted_by_name", label: "Submitted By" },
                      { k: "score", label: "Score" },
                      { k: "compliance_label", label: "Status" },
                      { k: "answered", label: "Answered" },
                      { k: "created_at", label: "Submitted" },
                      { k: null, label: "Action" },
                    ].map((col) => (
                      <th
                        key={col.label}
                        onClick={() => col.k && toggleSort(col.k)}
                        className={`px-5 py-3.5 text-left font-bold text-[11px] text-slate-600 uppercase tracking-wider whitespace-nowrap ${
                          col.k ? "cursor-pointer select-none" : "cursor-default"
                        }`}
                      >
                        <span className="inline-flex items-center gap-1">
                          {col.label}
                          {col.k && <SortIcon k={col.k} />}
                        </span>
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {sorted.map((row, i) => {
                    const sc = row.score ?? 0;
                    const total = row.total_questions ?? 43;
                    return (
                      <tr
                        key={row.id || i}
                        className={`border-b border-indigo-500/10 transition-colors hover:bg-indigo-50/50 ${
                          i % 2 === 0 ? "bg-white" : "bg-indigo-50/20"
                        }`}
                      >
                        <td className="px-3 py-3.5 text-xs font-semibold text-slate-500 tabular-nums">
                          {(page - 1) * PAGE_SIZE + i + 1}
                        </td>
                        <td className="px-5 py-3.5">
                          <div className="flex items-center gap-2.5">
                            <div className="w-[34px] h-[34px] rounded-[10px] shrink-0 bg-gradient-to-br from-indigo-50 to-indigo-100 border border-indigo-500/15 flex items-center justify-center">
                              <Building2 size={15} className="text-indigo-500" />
                            </div>
                            <div>
                              <div className="font-semibold text-slate-900">
                                {row.org_name || "Unnamed Organisation"}
                              </div>
                            </div>
                          </div>
                        </td>
                        <td className="px-5 py-3.5">
                          <span className="text-xs font-semibold text-slate-700">
                            {row.department_name || "—"}
                          </span>
                        </td>
                        <td className="px-5 py-3.5">
                          <span className="text-xs text-slate-600">
                            {row.submitted_by_name || "—"}
                          </span>
                        </td>
                        <td className="px-5 py-3.5">
                          <div className="flex items-center gap-2.5">
                            <div className="relative shrink-0">
                              <ArcGauge
                                value={sc}
                                size={40}
                                stroke={4}
                                color={scoreColor(sc)}
                              />
                              <div className="absolute inset-0 flex items-center justify-center">
                                <span
                                  className="text-[9px] font-extrabold"
                                  style={{ color: scoreColor(sc) }}
                                >
                                  {sc}%
                                </span>
                              </div>
                            </div>
                            <div className="w-[60px] h-1.5 rounded-full bg-slate-100 overflow-hidden">
                              <div
                                className="h-full rounded-full"
                                style={{ width: `${sc}%`, background: scoreColor(sc) }}
                              />
                            </div>
                          </div>
                        </td>
                        <td className="px-5 py-3.5">
                          <span
                            className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-[11px] font-bold"
                            style={{
                              background: scoreBg(sc),
                              color: scoreColor(sc),
                              border: `1px solid ${scoreColor(sc)}30`,
                            }}
                          >
                            <span
                              className="w-1.5 h-1.5 rounded-full shrink-0"
                              style={{ background: scoreColor(sc) }}
                            />
                            {row.compliance_label || scoreLabel(sc)}
                          </span>
                        </td>
                        <td className="px-5 py-3.5">
                          <span className="font-semibold text-slate-900">
                            {row.answered ?? "—"}
                          </span>
                          <span className="text-slate-400 text-[11px]"> / {total}</span>
                        </td>
                        <td className="px-5 py-3.5">
                          <div className="flex items-center gap-1.5">
                            <Calendar size={12} className="text-slate-400" />
                            <span className="text-slate-600 text-xs">
                              {row.created_at
                                ? new Date(row.created_at).toLocaleDateString("en-IN", {
                                  day: "numeric",
                                  month: "short",
                                  year: "numeric",
                                })
                                : "—"}
                            </span>
                          </div>
                        </td>
                        <td className="px-5 py-3.5">
                          <button
                            onClick={() => setSelected(row)}
                            className="inline-flex items-center gap-1.5 px-3.5 py-1.5 rounded-[10px] bg-gradient-to-br from-indigo-500 to-violet-500 text-white text-[11px] font-semibold border-0 cursor-pointer shadow-md shadow-indigo-500/30"
                          >
                            <Eye size={12} /> View
                          </button>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
              {total > 0 && (
                <div className="px-4 py-3 border-t border-slate-200 bg-white">
                  <Pagination
                    page={page}
                    pages={pages}
                    total={total}
                    pageSize={PAGE_SIZE}
                    onPageChange={setPage}
                    itemLabel="submissions"
                  />
                </div>
              )}
            </>
          )}
        </div>
      </div>

      {selected && (
        <SubmissionDetailModal
          submission={selected}
          onClose={() => {
            setSelected(null);
            if (searchParams.get("id")) {
              const next = new URLSearchParams(searchParams);
              next.delete("id");
              setSearchParams(next, { replace: true });
            }
          }}
        />
      )}
    </div>
  );
}

function buildGapSectionScores(submission) {
  const result = submission.result || {};
  const answers = submission.answers || {};
  const backendSections = result.sections || [];
  if (backendSections.length) {
    return backendSections.map((ss) => ({
      title: ss.title,
      score: ss.score ?? 0,
    }));
  }
  return SECTIONS.map((s) => {
    const yes = s.questions.filter((q) => answers[q.id] === "Yes").length;
    const partial = s.questions.filter((q) => answers[q.id] === "Partially").length;
    const na = s.questions.filter((q) => answers[q.id] === "N/A").length;
    const applicable = s.questions.length - na;
    const pct =
      applicable > 0 ? Math.round(((yes + partial * 0.5) / applicable) * 100) : 0;
    return { title: s.title, score: pct };
  });
}

function buildGapExportSections(submission, { forExcel = false } = {}) {
  const sc = submission.score ?? 0;
  const result = submission.result || {};
  const answers = submission.answers || {};
  const notes = submission.notes || {};
  const owners = submission.owners || {};
  const sectionScores = buildGapSectionScores(submission);

  const allQaRows = SECTIONS.flatMap((sec) =>
    sec.questions.map((q) => ({
      section: sec.title,
      question_id: q.id,
      question: q.text,
      answer: answers[q.id] ?? (forExcel ? "—" : ""),
      note: plainTextNote(notes[q.id] ?? "") || "",
      owner: owners[q.id] ?? "",
    })),
  );

  const sections = [
    {
      heading: "Executive Summary",
      metrics: [
        {
          label: "Compliance Score",
          value: `${sc}%`,
          color: scoreColor(sc),
        },
        {
          label: "Total Questions",
          value: String(submission.total_questions ?? result.total_questions ?? "—"),
          color: "#7dd3fc",
        },
        {
          label: "Answered",
          value: String(submission.answered ?? "—"),
          color: "#10b981",
        },
        {
          label: "Critical Gaps",
          value: String(result.critical_gaps?.length ?? 0),
          color: "#ef4444",
        },
      ],
      charts: [
        {
          type: "donut",
          title: "Compliance Score",
          value: sc,
          suffix: "%",
          label: submission.compliance_label || scoreLabel(sc),
        },
        {
          type: "pie",
          title: "Response Distribution",
          labels: ["Yes", "Partial", "No", "N/A"],
          values: [
            result.yes ?? 0,
            result.partial ?? 0,
            result.no ?? 0,
            result.na ?? 0,
          ],
        },
      ],
      rows: [
        { label: "Organisation", value: submission.org_name },
        { label: "Department", value: submission.department_name || "—" },
        { label: "Compliance Label", value: submission.compliance_label || scoreLabel(sc) },
        {
          label: "Submitted",
          value: submission.created_at
            ? new Date(submission.created_at).toLocaleDateString("en-IN", {
              day: "numeric",
              month: "long",
              year: "numeric",
            })
            : "—",
        },
      ],
    },
    {
      heading: "Section Scores",
      charts: [
        {
          type: "bar_horizontal",
          title: "Compliance by Section",
          labels: sectionScores.map((s) => s.title),
          values: sectionScores.map((s) => s.score),
          suffix: "%",
          max_value: 100,
        },
      ],
      rows: sectionScores.map((s) => ({
        label: s.title,
        value: `${s.score}%`,
      })),
    },
  ];

  if (forExcel && allQaRows.length) {
    sections.push({
      heading: "All Questions and Answers",
      table: {
        headers: ["Section", "Question ID", "Question", "Answer", "Note", "Owner"],
        rows: allQaRows.map((r) => [
          r.section,
          r.question_id,
          r.question,
          r.answer,
          r.note,
          r.owner,
        ]),
      },
      qa: allQaRows.map((r) => ({
        section: r.section,
        question_id: r.question_id,
        question: `${r.question_id}: ${r.question}`,
        answer: r.answer,
        note: r.note,
        owner: r.owner,
      })),
    });
  }

  if (result.recommendations?.length) {
    sections.push({
      heading: "Comments & Evidence",
      qa: result.recommendations.map((rec, i) => gapRecommendationToExportQa(rec, i)),
    });
  }

  if (result.critical_gaps?.length) {
    sections.push({
      heading: "Critical Gaps",
      qa: result.critical_gaps.map((g) => ({
        question: `${g.id} — ${g.section_title}`,
        answer: g.text,
        note: g.dpdp_ref ? `DPDP Ref: ${g.dpdp_ref || g.section}` : undefined,
      })),
    });
  }

  SECTIONS.forEach((sec) => {
    sections.push({
      heading: sec.title,
      qa: sec.questions.map((q) => ({
        section: sec.title,
        question_id: q.id,
        question: `${q.id}: ${q.text}`,
        answer: answers[q.id] ?? (forExcel ? "—" : ""),
        note: notes[q.id] ?? (forExcel ? "" : undefined),
        owner: owners[q.id] ?? (forExcel ? "" : undefined),
      })),
    });
  });

  return sections;
}

function SubmissionDetailModal({ submission, onClose }) {
  const [activeTab, setActiveTab] = useState("overview");
  const [exportingPdf, setExportingPdf] = useState(false);
  const [exportingExcel, setExportingExcel] = useState(false);
  const [exportingEvidence, setExportingEvidence] = useState("");
  const [attachmentsByQuestion, setAttachmentsByQuestion] = useState({});
  const [attachmentsLoading, setAttachmentsLoading] = useState(false);

  useEffect(() => {
    setActiveTab("overview");
  }, [submission]);

  useEffect(() => {
    if (!submission?.department_id) {
      setAttachmentsByQuestion({});
      return undefined;
    }
    let cancelled = false;
    setAttachmentsLoading(true);
    (async () => {
      try {
        const params = new URLSearchParams({
          department_id: submission.department_id,
        });
        if (submission.cycle_id) params.set("cycle_id", submission.cycle_id);
        if (submission.user_id) params.set("user_id", submission.user_id);
        const res = await apiFetch(`/gap-assessment/attachments?${params}`);
        if (!res.ok) throw new Error("Failed to load attachments");
        const rows = await res.json();
        if (cancelled) return;
        const grouped = {};
        (rows || []).forEach((file) => {
          if (!file?.question_id) return;
          if (!grouped[file.question_id]) grouped[file.question_id] = [];
          grouped[file.question_id].push(file);
        });
        setAttachmentsByQuestion(grouped);
      } catch {
        if (!cancelled) setAttachmentsByQuestion({});
      } finally {
        if (!cancelled) setAttachmentsLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [submission?.id, submission?.department_id, submission?.cycle_id, submission?.user_id]);

  const sc = submission.score ?? 0;
  const result = submission.result || {};
  const sections = result.sections || [];
  const criticalGaps = result.critical_gaps || [];
  const recommendations = result.recommendations || [];
  const answers = submission.answers || {};
  const notes = submission.notes || {};
  const owners = submission.owners || {};

  const tabs = [
    { id: "overview", label: "Overview" },
    { id: "sections", label: "Section Scores" },
    {
      id: "gaps",
      label: `Critical Gaps ${criticalGaps.length > 0 ? `(${criticalGaps.length})` : ""}`,
    },
    { id: "responses", label: "All Responses" },
  ];

  const getExportPayload = (forExcel = false) => {
    const orgName = submission.org_name || "Gap_Assessment";
    return {
      title: orgName,
      subtitle: `Gap Assessment · ${submission.created_at ? new Date(submission.created_at).toLocaleDateString("en-IN") : ""}`,
      module: "Gap Assessment",
      organization: orgName,
      filename: `Gap_Assessment_${safeExportFilename(orgName)}`,
      sections: buildGapExportSections(submission, { forExcel }),
    };
  };

  const handleExportPdf = async () => {
    setExportingPdf(true);
    try {
      await exportSubmissionPdf(getExportPayload(false));
    } catch (err) {
      showError(err?.message || "Failed to export PDF");
    } finally {
      setExportingPdf(false);
    }
  };

  const handleExportExcel = async () => {
    setExportingExcel(true);
    try {
      await exportSubmissionExcel(getExportPayload(true));
    } catch (err) {
      showError(err?.message || "Failed to export Excel");
    } finally {
      setExportingExcel(false);
    }
  };

  const handleEvidenceExport = async (format) => {
    setExportingEvidence(format);
    try {
      if (format === "pdf") {
        await exportGapEvidencePdf(submission, attachmentsByQuestion);
      } else {
        await exportGapEvidenceExcel(submission, attachmentsByQuestion);
      }
    } catch (err) {
      showError(err?.message || "Failed to export comments and evidence");
    } finally {
      setExportingEvidence("");
    }
  };

  return (
    <Modal open onClose={onClose} bare zIndex={200} align="top">
      <div className="w-full max-w-[860px] bg-white rounded-[20px] border border-indigo-500/10 shadow-2xl shadow-slate-900/15 font-sans overflow-hidden">
        <div
          style={{
            padding: "20px 24px 0",
            borderBottom: "1px solid rgba(99,102,241,0.1)",
          }}
        >
          <div
            style={{
              display: "flex",
              alignItems: "flex-start",
              gap: 16,
              marginBottom: 16,
              flexWrap: "wrap",
            }}
          >
            <div
              style={{
                width: 44,
                height: 44,
                borderRadius: 12,
                flexShrink: 0,
                background: "linear-gradient(135deg, #eef2ff, #e0e7ff)",
                border: "1px solid rgba(99,102,241,0.15)",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              <Building2 size={20} color="#6366f1" />
            </div>
            <div style={{ flex: 1, minWidth: 200 }}>
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: 12,
                  flexWrap: "wrap",
                }}
              >
                <h1
                  style={{
                    fontSize: 20,
                    fontWeight: 800,
                    color: "#0f172a",
                    margin: 0,
                  }}
                >
                  {submission.org_name || "Unnamed Organisation"}
                </h1>
                <ScoreRing
                  value={sc}
                  size={52}
                  stroke={4}
                  color={scoreColor(sc)}
                />
              </div>
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: 12,
                  marginTop: 8,
                  flexWrap: "wrap",
                }}
              >
                <span
                  style={{
                    display: "inline-flex",
                    alignItems: "center",
                    gap: 5,
                    padding: "4px 12px",
                    borderRadius: 20,
                    fontSize: 12,
                    fontWeight: 700,
                    background: scoreBg(sc),
                    color: scoreColor(sc),
                    border: `1px solid ${scoreColor(sc)}40`,
                  }}
                >
                  <span
                    style={{
                      width: 7,
                      height: 7,
                      borderRadius: "50%",
                      background: scoreColor(sc),
                    }}
                  />
                  {submission.compliance_label || scoreLabel(sc)}
                </span>
                {submission.department_name && (
                  <span style={{ fontSize: 12, color: "#6366f1", fontWeight: 600 }}>
                    Department: {submission.department_name}
                  </span>
                )}
                <span style={{ fontSize: 12, color: "#64748b" }}>
                  {submission.created_at
                    ? new Date(submission.created_at).toLocaleDateString("en-IN", {
                      day: "numeric",
                      month: "long",
                      year: "numeric",
                    })
                    : ""}
                </span>
              </div>
            </div>
            <div style={{ display: "flex", alignItems: "center", gap: 8, flexShrink: 0 }}>
              <button
                type="button"
                onClick={handleExportPdf}
                disabled={exportingPdf || exportingExcel || Boolean(exportingEvidence)}
                style={{
                  display: "inline-flex",
                  alignItems: "center",
                  gap: 6,
                  padding: "7px 12px",
                  borderRadius: 10,
                  border: "1px solid rgba(99,102,241,0.2)",
                  background: exportingPdf ? "#f1f5f9" : "#eef2ff",
                  color: "#4338ca",
                  fontSize: 12,
                  fontWeight: 600,
                  cursor: exportingPdf ? "wait" : "pointer",
                  opacity: exportingPdf ? 0.7 : 1,
                }}
              >
                <FileDown size={14} />
                {exportingPdf ? "Generating…" : "Export PDF"}
              </button>
              <button
                type="button"
                onClick={handleExportExcel}
                disabled={exportingExcel || exportingPdf || Boolean(exportingEvidence)}
                style={{
                  display: "inline-flex",
                  alignItems: "center",
                  gap: 6,
                  padding: "7px 12px",
                  borderRadius: 10,
                  border: "1px solid rgba(16,185,129,0.25)",
                  background: exportingExcel ? "#f1f5f9" : "#ecfdf5",
                  color: "#047857",
                  fontSize: 12,
                  fontWeight: 600,
                  cursor: exportingExcel ? "wait" : "pointer",
                  opacity: exportingExcel ? 0.7 : 1,
                }}
              >
                <FileSpreadsheet size={14} />
                {exportingExcel ? "Generating…" : "Excel"}
              </button>
              <button
                type="button"
                onClick={() => handleEvidenceExport("pdf")}
                disabled={exportingPdf || exportingExcel || Boolean(exportingEvidence)}
                style={{
                  display: "inline-flex",
                  alignItems: "center",
                  gap: 6,
                  padding: "7px 12px",
                  borderRadius: 10,
                  border: "1px solid rgba(99,102,241,0.2)",
                  background: exportingEvidence === "pdf" ? "#f1f5f9" : "#eef2ff",
                  color: "#4338ca",
                  fontSize: 12,
                  fontWeight: 600,
                  cursor: exportingEvidence === "pdf" ? "wait" : "pointer",
                }}
              >
                <FileDown size={14} />
                {exportingEvidence === "pdf" ? "Generating…" : "Evidence PDF"}
              </button>
              <button
                type="button"
                onClick={() => handleEvidenceExport("excel")}
                disabled={exportingPdf || exportingExcel || Boolean(exportingEvidence)}
                style={{
                  display: "inline-flex",
                  alignItems: "center",
                  gap: 6,
                  padding: "7px 12px",
                  borderRadius: 10,
                  border: "1px solid rgba(16,185,129,0.25)",
                  background: exportingEvidence === "excel" ? "#f1f5f9" : "#ecfdf5",
                  color: "#047857",
                  fontSize: 12,
                  fontWeight: 600,
                  cursor: exportingEvidence === "excel" ? "wait" : "pointer",
                }}
              >
                <FileSpreadsheet size={14} />
                {exportingEvidence === "excel" ? "Generating…" : "Evidence Excel"}
              </button>
              <button
                type="button"
                onClick={onClose}
                style={{
                  width: 34,
                  height: 34,
                  borderRadius: 10,
                  border: "1px solid rgba(99,102,241,0.15)",
                  background: "#ffffff",
                  color: "#475569",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  cursor: "pointer",
                }}
              >
                <XIcon size={16} />
              </button>
            </div>
          </div>

          {activeTab === "overview" && (
            <div
              style={{
                display: "grid",
                gridTemplateColumns: "repeat(auto-fit, minmax(160px, 1fr))",
                gap: 10,
                marginBottom: 16,
              }}
            >
              {[
                {
                  tag: "Q",
                  title: "Total Questions",
                  value: submission.total_questions ?? result.total_questions ?? "—",
                  accent: "#7dd3fc",
                  icon: ClipboardList,
                },
                {
                  tag: "ANS",
                  title: "Answered",
                  value: submission.answered ?? "—",
                  accent: "#6ee7b7",
                  icon: Check,
                },
                {
                  tag: "YES",
                  title: "Yes",
                  value: result.yes ?? "—",
                  accent: "#34d399",
                  icon: Check,
                },
                {
                  tag: "PRT",
                  title: "Partial",
                  value: result.partial ?? "—",
                  accent: "#fcd34d",
                  icon: Minus,
                },
                {
                  tag: "NO",
                  title: "No / Gaps",
                  value: result.no ?? "—",
                  accent: "#fca5a5",
                  icon: X,
                },
                {
                  tag: "NA",
                  title: "N/A",
                  value: result.na ?? "—",
                  accent: "#94a3b8",
                  icon: HelpCircle,
                },
              ].map((card) => (
                <DashboardStatCard key={card.title} {...card} />
              ))}
            </div>
          )}

          <div
            style={{
              display: "flex",
              gap: 0,
              overflowX: "auto",
              borderBottom: "1px solid rgba(99,102,241,0.1)",
              marginBottom: 20,
            }}
          >
            {tabs.map((t) => (
              <button
                key={t.id}
                onClick={() => setActiveTab(t.id)}
                style={{
                  padding: "10px 16px",
                  border: "none",
                  borderBottom: `2px solid ${activeTab === t.id ? "#6366f1" : "transparent"}`,
                  background: "transparent",
                  cursor: "pointer",
                  fontSize: 12,
                  fontWeight: activeTab === t.id ? 700 : 500,
                  color: activeTab === t.id ? "#6366f1" : "#64748b",
                  whiteSpace: "nowrap",
                }}
              >
                {t.label}
              </button>
            ))}
          </div>
        </div>

        <div style={{ padding: "20px 24px 24px", maxHeight: "62vh", overflowY: "auto", scrollbarWidth: "thin" }}>
          {activeTab === "overview" && (
            <div>
              {recommendations.length > 0 && (
                <div
                  style={{
                    borderRadius: 16,
                    padding: 20,
                    background: "#ffffff",
                    border: "1px solid rgba(99,102,241,0.12)",
                    boxShadow: "0 4px 16px rgba(15,23,42,0.05)",
                  }}
                >
                  <h4
                    style={{
                      fontSize: 12,
                      fontWeight: 700,
                      color: "#1e293b",
                      textTransform: "uppercase",
                      letterSpacing: "0.07em",
                      marginBottom: 4,
                    }}
                  >
                    Comments & Evidence
                  </h4>
                  <p style={{ fontSize: 11, color: "#64748b", marginBottom: 12 }}>
                    Observations and files captured during the assessment
                  </p>
                  {recommendations.map((rec, i) => (
                    <div
                      key={isGapRecommendationObject(rec) ? rec.question_id || i : i}
                      style={{
                        display: "flex",
                        gap: 10,
                        marginBottom: 12,
                        alignItems: "flex-start",
                      }}
                    >
                      <div
                        style={{
                          width: 20,
                          height: 20,
                          borderRadius: 6,
                          background: "rgba(99,102,241,0.1)",
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          flexShrink: 0,
                          marginTop: 1,
                        }}
                      >
                        <span style={{ fontSize: 9, fontWeight: 800, color: "#6366f1" }}>
                          {i + 1}
                        </span>
                      </div>
                      <div style={{ flex: 1 }}>
                        <div style={{ fontSize: 12, fontWeight: 700, color: "#0f172a", marginBottom: 4 }}>
                          {gapRecommendationLabel(rec, i)}
                        </div>
                        {isGapRecommendationObject(rec) && rec.answer && (
                          <div style={{ fontSize: 11, color: "#6366f1", marginBottom: 4 }}>
                            Response: {rec.answer}
                          </div>
                        )}
                        <p style={{ fontSize: 12, color: "#334155", lineHeight: 1.6, margin: 0 }}>
                          {isGapRecommendationObject(rec) ? gapRecommendationSummary(rec) : rec}
                        </p>
                        {isGapRecommendationObject(rec) && rec.evidence?.length > 0 && (
                          <div style={{ marginTop: 8, display: "flex", flexDirection: "column", gap: 6 }}>
                            {rec.evidence.map((file) => (
                              <AttachmentPreviewRow
                                key={file.id}
                                file={file}
                                previewPath={gapAttachmentPreviewPath(file)}
                              />
                            ))}
                          </div>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              )}
              {recommendations.length === 0 && (
                <div
                  style={{
                    textAlign: "center",
                    padding: 48,
                    background: "#fff",
                    borderRadius: 16,
                    border: "1px solid rgba(99,102,241,0.1)",
                  }}
                >
                  <CheckCircle2
                    size={36}
                    style={{ color: "#10b981", margin: "0 auto 12px", display: "block" }}
                  />
                  <p style={{ fontSize: 14, fontWeight: 600, color: "#047857" }}>
                    No comments or evidence recorded
                  </p>
                </div>
              )}
            </div>
          )}

          {activeTab === "sections" && (
            <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
              {(sections.length > 0
                ? sections
                : SECTIONS.map((s) => {
                  const yes = s.questions.filter((q) => answers[q.id] === "Yes").length;
                  const partial = s.questions.filter(
                    (q) => answers[q.id] === "Partially"
                  ).length;
                  const na = s.questions.filter((q) => answers[q.id] === "N/A").length;
                  const applicable = s.questions.length - na;
                  const pct =
                    applicable > 0
                      ? Math.round(((yes + partial * 0.5) / applicable) * 100)
                      : 0;
                  return {
                    id: s.id,
                    title: s.title,
                    score: pct,
                    yes,
                    partial,
                    no: s.questions.filter((q) => answers[q.id] === "No").length,
                    na,
                    total: s.questions.length,
                    risk_level: pct >= 75 ? "Low" : pct >= 40 ? "Medium" : "High",
                  };
                })
              ).map((ss) => {
                const secDef = SECTION_MAP[ss.id];
                const accent = secDef?.accent || "#6366f1";
                const Icon = secDef?.icon || Shield;
                const riskColor =
                  ss.risk_level === "Low"
                    ? "#10b981"
                    : ss.risk_level === "Medium"
                      ? "#f59e0b"
                      : "#ef4444";
                return (
                  <div
                    key={ss.id}
                    style={{
                      borderRadius: 16,
                      padding: "16px 20px",
                      background: "#ffffff",
                      border: "1px solid rgba(99,102,241,0.1)",
                      boxShadow: "0 2px 8px rgba(0,0,0,0.04)",
                    }}
                  >
                    <div
                      style={{
                        display: "flex",
                        alignItems: "center",
                        gap: 12,
                        marginBottom: 12,
                      }}
                    >
                      <div
                        style={{
                          width: 36,
                          height: 36,
                          borderRadius: 10,
                          flexShrink: 0,
                          background: `linear-gradient(135deg, ${accent}dd, ${accent}88)`,
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          boxShadow: `0 3px 10px ${accent}35`,
                        }}
                      >
                        <Icon size={16} color="#fff" />
                      </div>
                      <div style={{ flex: 1 }}>
                        <div style={{ fontWeight: 700, fontSize: 13, color: "#0f172a" }}>
                          {ss.title}
                        </div>
                        <div style={{ fontSize: 11, color: "#94a3b8", marginTop: 1 }}>
                          {ss.total} questions · Risk:{" "}
                          <span style={{ color: riskColor, fontWeight: 600 }}>
                            {ss.risk_level}
                          </span>
                        </div>
                      </div>
                      <span
                        style={{
                          fontSize: 18,
                          fontWeight: 800,
                          color: scoreColor(ss.score),
                        }}
                      >
                        {ss.score}%
                      </span>
                    </div>
                    <div
                      style={{
                        height: 6,
                        borderRadius: 9,
                        background: "#f1f5f9",
                        overflow: "hidden",
                      }}
                    >
                      <div
                        style={{
                          width: `${ss.score}%`,
                          height: "100%",
                          background: scoreColor(ss.score),
                          borderRadius: 9,
                        }}
                      />
                    </div>
                    <div style={{ display: "flex", gap: 16, marginTop: 10 }}>
                      {[
                        ["Yes", ss.yes, "#047857"],
                        ["Partial", ss.partial, "#b45309"],
                        ["No", ss.no, "#b91c1c"],
                        ["N/A", ss.na, "#475569"],
                      ].map(([l, v, c]) => (
                        <span key={l} style={{ fontSize: 11, color: c, fontWeight: 600 }}>
                          {v} {l}
                        </span>
                      ))}
                    </div>
                  </div>
                );
              })}
            </div>
          )}

          {activeTab === "gaps" && (
            <div>
              {criticalGaps.length === 0 ? (
                <div
                  style={{
                    textAlign: "center",
                    padding: 48,
                    background: "#fff",
                    borderRadius: 16,
                  }}
                >
                  <CheckCircle2
                    size={40}
                    style={{ color: "#10b981", margin: "0 auto 12px", display: "block" }}
                  />
                  <p style={{ fontSize: 14, fontWeight: 600, color: "#047857" }}>
                    No critical gaps found
                  </p>
                  <p style={{ fontSize: 12, color: "#94a3b8", marginTop: 4 }}>
                    All high-severity items are addressed
                  </p>
                </div>
              ) : (
                <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
                  {criticalGaps.map((g) => (
                    <div
                      key={g.id}
                      style={{
                        borderRadius: 14,
                        padding: "14px 18px",
                        background: "#fff1f2",
                        border: "1px solid #fecdd3",
                      }}
                    >
                      <div
                        style={{
                          display: "flex",
                          alignItems: "center",
                          gap: 8,
                          marginBottom: 6,
                        }}
                      >
                        <span
                          style={{
                            fontSize: 10,
                            fontFamily: "monospace",
                            fontWeight: 700,
                            padding: "2px 7px",
                            borderRadius: 6,
                            background: "#fecdd3",
                            color: "#b91c1c",
                          }}
                        >
                          {g.id}
                        </span>
                        <span style={{ fontSize: 11, fontWeight: 600, color: "#0f172a" }}>
                          {g.section_title}
                        </span>
                        <span
                          style={{
                            fontSize: 9,
                            padding: "2px 8px",
                            borderRadius: 20,
                            background: "#fee2e2",
                            color: "#b91c1c",
                            fontWeight: 600,
                            marginLeft: "auto",
                          }}
                        >
                          High Risk
                        </span>
                      </div>
                      <p
                        style={{
                          fontSize: 12,
                          color: "#334155",
                          lineHeight: 1.55,
                          margin: 0,
                        }}
                      >
                        {g.text}
                      </p>
                      <div style={{ fontSize: 10, color: "#94a3b8", marginTop: 6 }}>
                        DPDP Ref: {g.dpdp_ref || g.section}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}

          {activeTab === "responses" && (
            <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
              {SECTIONS.map((sec) => (
                <div key={sec.id} style={{ marginBottom: 8 }}>
                  <div
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: 8,
                      padding: "10px 14px",
                      borderRadius: 12,
                      background: `linear-gradient(135deg, ${sec.accent}12, ${sec.accent}06)`,
                      border: `1px solid ${sec.accent}25`,
                      marginBottom: 6,
                    }}
                  >
                    <sec.icon size={13} style={{ color: sec.accent }} />
                    <span style={{ fontSize: 12, fontWeight: 700, color: "#0f172a" }}>
                      {sec.title}
                    </span>
                  </div>
                  {sec.questions.map((q) => {
                    const ans = answers[q.id];
                    const cfg = ans ? R_CFG[ans] : null;
                    const note = notes[q.id];
                    const owner = owners[q.id];
                    return (
                      <div
                        key={q.id}
                        style={{
                          borderRadius: 12,
                          padding: "12px 16px",
                          marginBottom: 4,
                          background: "#ffffff",
                          border: `1px solid ${ans ? cfg?.border + "80" : "rgba(99,102,241,0.08)"}`,
                          boxShadow: ans ? `0 1px 6px ${cfg?.color}15` : "none",
                        }}
                      >
                        <div
                          style={{ display: "flex", alignItems: "flex-start", gap: 10 }}
                        >
                          <span
                            style={{
                              fontSize: 9,
                              fontFamily: "monospace",
                              fontWeight: 700,
                              padding: "2px 6px",
                              borderRadius: 5,
                              background: "#f1f5f9",
                              color: "#475569",
                              flexShrink: 0,
                              marginTop: 2,
                            }}
                          >
                            {q.id}
                          </span>
                          <p
                            style={{
                              fontSize: 12,
                              color: "#334155",
                              flex: 1,
                              lineHeight: 1.5,
                              margin: 0,
                            }}
                          >
                            {q.text}
                          </p>
                          {ans ? (
                            <span
                              style={{
                                display: "inline-flex",
                                alignItems: "center",
                                gap: 4,
                                padding: "3px 10px",
                                borderRadius: 20,
                                fontSize: 11,
                                fontWeight: 700,
                                background: cfg?.bg,
                                color: cfg?.color,
                                border: `1px solid ${cfg?.border}`,
                                flexShrink: 0,
                              }}
                            >
                              {ans}
                            </span>
                          ) : (
                            <span
                              style={{ fontSize: 11, color: "#cbd5e1", flexShrink: 0 }}
                            >
                              —
                            </span>
                          )}
                        </div>
                        {(note || owner) && (
                          <div
                            style={{
                              marginTop: 8,
                              paddingTop: 8,
                              borderTop: "1px solid rgba(99,102,241,0.08)",
                              display: "flex",
                              gap: 16,
                              flexWrap: "wrap",
                            }}
                          >
                            {note && (
                              <div style={{ fontSize: 11, color: "#475569", flex: 1, minWidth: 0 }}>
                                <span style={{ fontWeight: 600, color: "#1e293b" }}>
                                  Comment:
                                </span>{" "}
                                {plainTextNote(note)}
                              </div>
                            )}
                            {owner && (
                              <span style={{ fontSize: 11, color: "#475569" }}>
                                <span style={{ fontWeight: 600, color: "#1e293b" }}>
                                  Owner:
                                </span>{" "}
                                {owner}
                              </span>
                            )}
                          </div>
                        )}
                        {(attachmentsByQuestion[q.id] || []).length > 0 && (
                          <div style={{ marginTop: 8, display: "flex", flexDirection: "column", gap: 6 }}>
                            {(attachmentsByQuestion[q.id] || []).map((file) => (
                              <AttachmentPreviewRow
                                key={file.id}
                                file={file}
                                previewPath={gapAttachmentPreviewPath(file)}
                              />
                            ))}
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </Modal>
  );
}
